import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { usersApi } from '../api/users.api';
import { User } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const roleColors: Record<string, 'primary' | 'secondary' | 'warning' | 'info' | 'success'> = {
  admin: 'primary',
  mozo: 'secondary',
  cajero: 'warning',
  almacen: 'info',
  cocinero: 'success',
  barman: 'primary',
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState(0);

  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('mozo');

  const loadUsers = async () => {
    try {
      const res = await usersApi.getAll();
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setRole('mozo');
    setEditId(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditId(user.id);
    setName(user.fullName);
    setEmail(user.email);
    setRole(user.role);
    setPassword('');
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEditing && editId) {
        const data: any = { fullName: name, email, role };
        if (password) data.password = password;
        await usersApi.update(editId, data);
      } else {
        await usersApi.create({ fullName: name, email, password, role });
      }
      setModalOpen(false);
      resetForm();
      loadUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await usersApi.delete(deleteId);
      setDeleteDialogOpen(false);
      loadUsers();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Usuarios</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nuevo Usuario
        </Button>
      </Box>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'ID', minWidth: 60 },
          { id: 'fullName', label: 'Nombre', sx: { fontWeight: 500 } },
          { id: 'email', label: 'Email' },
          {
            id: 'role', label: 'Rol',
            format: (value: string) => (
              <Chip label={value} size="small" color={roleColors[value] || 'default'} />
            ),
          },
          {
            id: 'isActive', label: 'Estado',
            format: (value: any) => (
              <Chip
                label={value !== false ? 'Activo' : 'Inactivo'}
                size="small"
                color={value !== false ? 'success' : 'default'}
                variant="outlined"
              />
            ),
          },
        ]}
        rows={users}
        searchFields={['fullName', 'email', 'role']}
        exportFileName="usuarios"
        emptyMessage="No hay usuarios registrados"
        renderActions={(u) => (
          <>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(u)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(u.id); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField
                fullWidth label="Nombre" value={name}
                onChange={(e) => setName(e.target.value)} required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth label="Email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={isEditing ? 'Nueva contraseña (dejar vacio para no cambiar)' : 'Contraseña'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!isEditing}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select value={role} label="Rol" onChange={(e) => setRole(e.target.value)}>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="mozo">Mozo</MenuItem>
                  <MenuItem value="cajero">Cajero</MenuItem>
                  <MenuItem value="almacen">Almacen</MenuItem>
                  <MenuItem value="cocinero">Cocinero</MenuItem>
                  <MenuItem value="barman">Barman</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Usuario"
        message="¿Estas seguro de que deseas eliminar este usuario?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default Users;
